"use client";

import { c as _c } from "react/compiler-runtime";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import { getQuickVoteAbsoluteRemainingPower, getQuickVoteRatingRange, isMemesQuickVoteVoteableDrop, type MemesQuickVoteStats } from "@/hooks/memesQuickVote.helpers";
import { fetchMemesQuickVoteUndiscoveredDrop, getMemesQuickVoteUndiscoveredDropQueryKey } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useQuery } from "@tanstack/react-query";
type MemesWaveFooterStats = MemesQuickVoteStats & {
  readonly isAvailable: boolean;
  readonly isReady: boolean;
};
const EMPTY_STATS: MemesWaveFooterStats = {
  isAvailable: false,
  isReady: false,
  leftThisRoundCount: 0,
  uncastPower: null,
  unratedCount: 0,
  votingLabel: null
};
export const useMemesWaveFooterStats = () => {
  const $ = _c(26);
  const {
    contextProfile,
    isEnabled,
    memesWaveId,
    proxyId
  } = useMemesQuickVoteContext();
  const waveId = typeof memesWaveId === "string" && memesWaveId.length > 0 ? memesWaveId : null;
  let t0;
  if ($[0] !== contextProfile || $[1] !== proxyId || $[2] !== waveId) {
    t0 = getMemesQuickVoteUndiscoveredDropQueryKey({
      contextProfile,
      proxyId,
      sessionId: 0,
      skip: 0,
      waveId
    });
    $[0] = contextProfile;
    $[1] = proxyId;
    $[2] = waveId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = isEnabled && waveId !== null;
  let t2;
  if ($[4] !== waveId) {
    t2 = t3 => {
      const {
        signal
      } = t3;
      return fetchMemesQuickVoteUndiscoveredDrop({
        skip: 0,
        signal,
        waveId
      });
    };
    $[4] = waveId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getDefaultQueryRetry();
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t4 = {
      queryKey: t0,
      enabled: t1,
      queryFn: t2,
      staleTime: 60000,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...t3
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const query = useQuery(t4);
  const activeDrop = query.data?.drop ?? null;
  let t5;
  if ($[11] !== activeDrop) {
    t5 = activeDrop ? getQuickVoteAbsoluteRemainingPower(getQuickVoteRatingRange(activeDrop)) : null;
    $[11] = activeDrop;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const uncastPower = t5;
  let t6;
  if ($[13] !== activeDrop || $[14] !== isEnabled || $[15] !== query.isSuccess || $[16] !== waveId) {
    t6 = isEnabled && waveId !== null && (!query.isSuccess || isMemesQuickVoteVoteableDrop(activeDrop));
    $[13] = activeDrop;
    $[14] = isEnabled;
    $[15] = query.isSuccess;
    $[16] = waveId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const isAvailable = t6;
  if (!query.isSuccess || !activeDrop || !isMemesQuickVoteVoteableDrop(activeDrop) || typeof uncastPower !== "number") {
    let t7;
    if ($[18] !== isAvailable) {
      t7 = {
        ...EMPTY_STATS,
        isAvailable
      };
      $[18] = isAvailable;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  const t7 = WAVE_VOTING_LABELS[activeDrop.wave.voting_credit_type];
  let t8;
  if ($[20] !== isAvailable || $[21] !== query.data.left_to_vote_in_current_round || $[22] !== query.data.total_count || $[23] !== t7 || $[24] !== uncastPower) {
    t8 = {
      isAvailable,
      leftThisRoundCount: query.data.left_to_vote_in_current_round,
      uncastPower,
      unratedCount: query.data.total_count,
      votingLabel: t7,
      isReady: true
    };
    $[20] = isAvailable;
    $[21] = query.data.left_to_vote_in_current_round;
    $[22] = query.data.total_count;
    $[23] = t7;
    $[24] = uncastPower;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};